import { Chip, Tooltip } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"

/**
 * One demand, carrying its delta state wherever it appears.
 *
 * Three states, not two, because "forced" is materially more serious than "added": a forced
 * demand is present ONLY because an override overrules a live decision — the client's own role
 * assignment, or the environment's feature gate. Colouring it the same green as an ordinary
 * override-added demand would hide exactly the thing worth reviewing.
 *
 * The highlight is deliberately not confined to the delta view: in the full demand list these
 * pills are the only way to see which entries the overrides and the pipeline are responsible
 * for. Every highlighted pill explains itself on hover. An explicit `color` still wins, so the
 * delta lists can force their own styling.
 */
export function DemandChip({ item: demand, color, variant = "outlined" }) {
    const { breakdown } = useBoundContext()
    const forced = (breakdown?.forced ?? []).find(matchesDemand)
    const added = (breakdown?.addedByOverrides ?? []).includes(demand)
    const removal = (breakdown?.removed ?? []).find(matchesDemand)

    return (
        <Tooltip title={explanation()} placement="top" arrow>
            <Chip
                size="small"
                label={demand}
                variant={forced || added || removal ? "filled" : variant}
                color={color ?? deltaColor()}
                data-cy={`access-demand-${demand}`}
                data-delta={deltaState()}
            />
        </Tooltip>
    )

    function explanation() {
        if (forced) {
            return `Forced through by an override. Would otherwise have been removed by ${forced.wouldBeRemovedBy}. ${forced.reason}`
        }
        if (added) return "Added by an override — this demand is not granted by any product the client holds."
        if (removal) return `Removed by ${removal.removedBy}. ${removal.reason}`
        return demand
    }

    function deltaState() {
        if (forced) return "forced"
        if (added) return "added"
        if (removal) return "removed"
        return undefined
    }

    function deltaColor() {
        if (forced) return "warning"
        if (added) return "success"
        if (removal) return "error"
        return "default"
    }

    function matchesDemand(entry) {
        return entry.demand === demand
    }
}
